// src/components/OtpInput.tsx
// 6-digit OTP code input with auto-focus and paste support.

import { useRef, useCallback } from 'react';
import { cn } from '@core/ui';

interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

export function OtpInput({ length = 6, value, onChange, disabled, error, className }: OtpInputProps) {
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const digits = value.split('').concat(Array(length).fill('')).slice(0, length);

  const focusInput = (index: number) => {
    inputsRef.current[index]?.focus();
  };

  const handleChange = useCallback(
    (index: number, char: string) => {
      if (!/^\d?$/.test(char)) return;
      const arr = digits.slice();
      arr[index] = char;
      const next = arr.join('');
      onChange(next);
      if (char && index < length - 1) focusInput(index + 1);
    },
    [digits, length, onChange],
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !digits[index] && index > 0) {
        focusInput(index - 1);
      }
      if (e.key === 'ArrowLeft' && index > 0) focusInput(index - 1);
      if (e.key === 'ArrowRight' && index < length - 1) focusInput(index + 1);
    },
    [digits, length],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
      if (pasted) {
        onChange(pasted.padEnd(length, '').slice(0, length));
        focusInput(Math.min(pasted.length, length - 1));
      }
    },
    [length, onChange],
  );

  return (
    <div className={cn('flex gap-2 justify-center', className)}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className={cn(
            'w-12 h-14 text-center text-xl font-semibold rounded-lg border',
            'bg-background text-foreground',
            'focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary',
            'transition-colors',
            error ? 'border-destructive' : 'border-border',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}
